import type { FC } from 'react';
import {
  superBundleOfferStatusLabels,
  superBundleQuantityUnitLabels,
} from 'src/apis/super-bundle-service/mappings';
import type {
  SuperBundleOfferDetails,
  SuperBundleOfferStatus,
} from 'src/apis/super-bundle-service/types';
import type { SuperBundleServiceRequest } from 'src/apis/super-bundle-service/use-super-bundle-service-root';
import { DataItems } from 'src/components/data-display/data-list';
import { EmptyCard } from 'src/components/data-display/empty-card';
import {
  StatusDisplay,
  type StatusProps,
} from 'src/components/data-display/status-display';
import { PageHeader } from 'src/components/layout/page-header';
import { Link } from 'src/components/navigation/link';
import type { Successful } from 'src/hooks/use-axios';
import { useSaveReferrer } from 'src/hooks/use-referrer';
import {
  useTimer,
  FormatLngAndSuperBundleTimerForTable,
} from 'src/hooks/use-timer';

const superBundleOfferStatusVariantMap: Record<
  SuperBundleOfferStatus,
  StatusProps['variant']
> = {
  OPEN: 'ready',
  FINISHED: 'success',
  CANCELLED: 'inactive',
  FAILED: 'error',
  DELETED: 'error',
  CREATED: 'warning',
  PUBLISHED: 'warning',
};

export const OfferStatusWithTimer: FC<{
  status: SuperBundleOfferStatus;
  countdownEnd?: string | null;
  superBundleMarketingRoot: Successful<SuperBundleServiceRequest>;
}> = ({ status, countdownEnd, superBundleMarketingRoot }) => {
  return (
    <StatusDisplay
      variant={superBundleOfferStatusVariantMap[status]}
      status={superBundleOfferStatusLabels[status]}
      appendix={
        countdownEnd && (
          <Countdown
            timestamp={countdownEnd}
            superBundleMarketingRoot={superBundleMarketingRoot}
          />
        )
      }
    />
  );
};

const Countdown: FC<{
  timestamp: string;
  superBundleMarketingRoot: Successful<SuperBundleServiceRequest>;
}> = ({ timestamp, superBundleMarketingRoot }) => {
  const timer = useTimer(timestamp);

  if (!timer) return null;
  return (
    <FormatLngAndSuperBundleTimerForTable
      timer={timer}
      superBundleMarketingRoot={superBundleMarketingRoot}
    />
  );
};

export const OperatorProposals: FC<{
  proposals: SuperBundleOfferDetails['operatorProposals'];
}> = ({ proposals }) => {
  return proposals.map((proposal) => (
    <DataItems key={proposal.operatorId}>
      <span>{proposal.operatorName}</span>
      <span data-testid="reservePrice">
        {proposal.reservePrice} {proposal.currency}/
        {superBundleQuantityUnitLabels[proposal.quantityUnit]}
      </span>
    </DataItems>
  ));
};

export const OfferNotFound: FC<{
  superBundleServiceRoot: Successful<SuperBundleServiceRequest>;
  title?: string;
}> = ({ superBundleServiceRoot, title = 'Super Bundle' }) => {
  const savedReferrer = useSaveReferrer({ label: title });
  return (
    <>
      <PageHeader
        title={title}
        actions={
          superBundleServiceRoot.response.data._links.createOffer ? (
            <Link
              data-testid="create-offer-button"
              mode="button-primary"
              {...savedReferrer.getLinkProps(
                '/transport/super-bundle/offers/create'
              )}
            >
              Create Offer
            </Link>
          ) : undefined
        }
      />
      <EmptyCard
        data-testid="no-active-offer-found-text"
        label="The requested offer could not be found. It may have already ended or been deleted. Please check the reporting section for the results."
        link={{
          to: '/reporting/super-bundle',
          children: 'Reporting section',
        }}
      />
    </>
  );
};
